import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import {
    fetchGreyPurchaseOrders,
    getGreyPurchaseOrders,
} from '../../app/reducers/PurchaseOrder/greyPurchaseOrderSlice';
import PageWithCard from '../../components/infrastructure/PageWithCard';
import TableWithHeadingAndGlobalSearch from '../../components/Table/TableWithHeadingAndGlobalSearch';
import PaginationClassic from '../../components/pagination/PaginationClassic';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import { Trash } from 'react-feather';
import {
    deleteGreyOrder,
    disconnectGreyOrder,
} from '../../app/api/GreyReceive';
import PrimaryButton from '../../components/infrastructure/Buttons/PrimaryButton';

const ViewReceiveGrayPurchaseOrder = () => {
    const [page, setPage] = useState(1);
    const [poDetailData, setPoDetailData] = useState(null);
    const [isPODetailModalOpen, setIsPODetailModalOpen] = useState(false);

    const { greyPurchaseOrder, loading } = useSelector(getGreyPurchaseOrders);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchGreyPurchaseOrders({ populate: true, page }));
    }, [page]);

    const columns = [
        {
            Header: 'SystemId',
            accessor: '_id',
        },
        {
            Header: 'Purchase Order',
            accessor: 'purchaseOrderNumber',
        },
        {
            Header: 'ZOHO Error',
            accessor: 'zohoError',
        },
        {
            Header: 'Connected to zoho',
            accessor: 'postToZoho',
        },
        {
            Header: 'Vendor Name',
            accessor: 'vendorName',
        },
        {
            Header: 'Broker Name',
            accessor: 'brokerName',
        },
        {
            Header: 'Master Grey Vendor',
            accessor: 'master_grey_vendor_name',
        },
        {
            Header: 'bill No',
            accessor: 'billNo',
        },
        {
            Header: 'Quality',
            Cell: ({ row }) => {
                return <span>{row?.original?.quality?.name}</span>;
            },
        },
        {
            Header: 'Location',
            Cell: ({ row }) => {
                return <span>{row?.original?.location?.name}</span>;
            },
        },
        {
            Header: 'Po Detail',
            Cell: ({ row }) => {
                return (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsPODetailModalOpen(true);
                            setPoDetailData(row?.original);
                        }}
                        className="text text-blue-400 hover:text-blue-700 cursor-pointer truncate"
                    >
                        View PO Detail
                    </div>
                );
            },
        },
        {
            Header: 'Action',
            Cell: ({ row }) => {
                return (
                    <div className="flex gap-2">
                        <PrimaryButton
                            onClick={async () => {
                                await disconnectGreyOrder(row.original._id);
                                dispatch(
                                    fetchGreyPurchaseOrders({
                                        populate: true,
                                        page,
                                    })
                                );
                            }}
                            disabled={row.original.postToZoho !== 'yes'}
                        >
                            Disconnect from Zoho
                        </PrimaryButton>

                        <Trash
                            color="red"
                            onClick={async (e) => {
                                await deleteGreyOrder(row.original._id);
                                dispatch(
                                    fetchGreyPurchaseOrders({
                                        populate: true,
                                        page,
                                    })
                                );
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    const dataMemo = useMemo(
        () => (greyPurchaseOrder?.docs ? greyPurchaseOrder?.docs : []),
        [greyPurchaseOrder]
    );

    const colsMemo = useMemo(() => columns ?? [], [columns]);

    const debouncedSearch = useCallback(
        _.debounce(async (search) => {
            dispatch(
                fetchGreyPurchaseOrders({
                    search: search,
                    populate: true,
                    page,
                })
            );
        }, 300),
        []
    );

    return (
        <PageWithCard>
            <PurchaseOrderDetailModal
                modalOpen={isPODetailModalOpen}
                setModalOpen={setIsPODetailModalOpen}
                data={poDetailData}
            />
            <TableWithHeadingAndGlobalSearch
                loading={loading}
                data={dataMemo}
                columns={colsMemo}
                heading={'View Grey Receive'}
                searchFunction={(search) => {
                    debouncedSearch(search);
                }}
            />
            <PaginationClassic
                paginationDetails={greyPurchaseOrder}
                setPage={setPage}
            />
        </PageWithCard>
    );
};

export default ViewReceiveGrayPurchaseOrder;
